
import { useEffect, useRef, useState } from 'react';
import DataTable from '../../../components/data-table/DataTable';
import { getAccounts } from '../../../services/accountsService';
import SearchTable from '../../../components/search-table/SearchTable';
import { StyledCashBoxes } from './cash-boxes.styled';
import { deleteEmptyFieldsFromObject, setAllValuesToNull } from '../../../helpers/helpers';
import { useSelector, useDispatch } from 'react-redux';
import { AccountStructure } from '../../../models/accounts-structure';
import { Account, AccountSchema, AccountSearch } from '../../../models/account';
import { RootState } from '../../../redux/store';
import { Skeleton } from '@mui/material';
import { CanceledError } from 'axios';
import ListItemIcon from '@mui/material/ListItemIcon';
import MovingIcon from '@mui/icons-material/Moving';
import EditIcon from '@mui/icons-material/Edit';
import { setCashBoxesSearch } from '../../../redux/features/browse';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from "@emotion/react";
import { useErrorBoundary } from "react-error-boundary";
import { handleApiErrors } from '../../../services/errorHandling';
import { useGetAccountStructureQuery } from '../../../redux/api/accounts-structure.api';

interface CashBoxesProps {
    searchFormData?: AccountSearch;
}

function CashBoxes({ searchFormData }: CashBoxesProps) {

    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch()
    const theme = useTheme();
    const { showBoundary } = useErrorBoundary();

    const { data: cashBoxesStructure } = useGetAccountStructureQuery("קופה");
    
    // const cashBoxesStructure = useSelector((state: RootState) => state.structure.cashBoxes)
    const cashBoxesSearch = useSelector((state: RootState) => state.browse.cashBoxesSearch)

    const [data, setData] = useState<Account[]>([]);
    const [searchStructure, setSearchStructure] = useState<AccountStructure[]>();
    const [tableStructure, setTableStructure] = useState<AccountStructure[]>();
    const [sortKey, setSortKey] = useState<string>('id');
    const [sortMethod, setSortMethod] = useState<string>('DESC');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const abortControllerRef = useRef<AbortController | null>(null);


    const options = [
        {
            label: "עריכה",
            icon: <ListItemIcon sx={{ color: theme.colors.main_color }}><EditIcon fontSize="small" /></ListItemIcon>,
            href: ""
        },
        {
            label: "תנועות",
            icon: <ListItemIcon sx={{ color: theme.colors.main_color }}><MovingIcon fontSize="small" /></ListItemIcon>,
            href: "journal-entries"
        },
    ];

    const cashBoxSearchForm = useForm<AccountSearch>({defaultValues:{
        "account_type": "קופה",
        ...cashBoxesSearch,
        ...searchFormData
    }});


    // useEffect(() => {
    //     if (!cashBoxesStructure) {
    //         getStructureAndSaveAtStore();
    //     }
    // }, []);

    useEffect(() => {
        setSearchStructure(cashBoxesStructure?.filter(field => field.show_search))
        setTableStructure(cashBoxesStructure?.filter(field => field.show_browse))
    }, [cashBoxesStructure]);


    useEffect(() => {
        if (sortKey && sortMethod) {
            cashBoxSearchForm.handleSubmit(onSearch)();
        }
    }, [sortKey, sortMethod]);


    useEffect(() => {
        cashBoxSearchForm.watch(() => {
            cashBoxSearchForm.handleSubmit(onSearch)();
        })
    }, [cashBoxSearchForm.watch]);



    function handleSortKeyChanged(value: string) {
        setSortKey(value);
    }

    function handleSortMethodChanged(value: string) {
        setSortMethod(value);
    }



    const onSearch: SubmitHandler<AccountSearch> = (data) => {
        dispatch(setCashBoxesSearch(data));
        getData(data);
    }


    async function getData(data: AccountSearch) {

        abortControllerRef.current?.abort();
        abortControllerRef.current = new AbortController();
        setIsLoading(true);

        try {
            const responseData = await getAccounts(deleteEmptyFieldsFromObject(data), sortKey || 'id', "10", sortMethod || 'ASC', ["customData", "entries.document.documentSettings"], abortControllerRef.current.signal);
            const cashBoxesParse = z.array(AccountSchema).parse(responseData);
            setData(cashBoxesParse || []);
            setIsLoading(false);
        } catch (error: unknown) {
            if (error instanceof CanceledError) {
                return;
            }
            handleApiErrors(error, showBoundary);
        }

    }


    function editSearchStructure(){
        navigate('/cash-boxes/structure/search', {
            replace: true,
            state: {
                breadcrumbData: [
                    {
                        name: "כרטסות",
                        link: "/cash-boxes",
                    },
                    {
                        name: "הגדרת מבנה חיפוש קופה",
                    }
                ]
            }
        });
    }

    function editBrowseStructure(){
        navigate('/cash-boxes/structure/browse', {
            replace: true,
            state: {
                breadcrumbData: [
                    {
                        name: "כרטסות",
                        link: "/cash-boxes",
                    },
                    {
                        name: "הגדרת מבנה דפדוף קופה",
                    }
                ]
            }
        });
    }

    function reset() {

        const values = setAllValuesToNull(cashBoxSearchForm.getValues());

        for (const key in values) {
            if (values.hasOwnProperty(key)) {
                cashBoxSearchForm.setValue(key as keyof AccountSearch, values[key as keyof AccountSearch]);
            }
        }

        cashBoxSearchForm.setValue("account_type", "קופה")
    }

    return (
        <StyledCashBoxes>
            {searchStructure ?
                <SearchTable structure={searchStructure} setStructure={editSearchStructure} reset={reset} useForm={cashBoxSearchForm} style={{ backgroundColor: "rgb(35, 48, 68)", padding: 5, marginButton: 20 }} /> :
                <Skeleton variant="rectangular" height={100} animation="wave" sx={{ bgcolor: theme.colors.section_color }} />}
            {tableStructure ?
                <DataTable structure={tableStructure} setStructure={editBrowseStructure} sortKey={sortKey} setSortKey={handleSortKeyChanged} sortMethod={sortMethod} setSortMethod={handleSortMethodChanged} data={data} isLoading={isLoading} options={options} /> :
                <Skeleton variant="rectangular" height={150} animation="wave" sx={{ bgcolor: theme.colors.section_color }} />}
        </StyledCashBoxes>
    );
}

export default CashBoxes;